import { useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";
import {
  Gauge,
  Lightbulb,
  Shuffle,
  Check,
  X as XIcon,
  RefreshCw,
  ArrowRight,
} from "@/lib/icons";
import {
  KIN_CATEGORY_LABELS,
  KIN_PROBLEMS,
  type KinCategory,
  type KinDifficulty,
  type KinProblem,
} from "@/data/kinetics";

const ALL_CATEGORIES = Object.keys(KIN_CATEGORY_LABELS) as KinCategory[];
const ALL_DIFFICULTIES: KinDifficulty[] = ["easy", "medium", "hard"];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface AttemptState {
  problemId: string;
  selected: number | null;
  graded: boolean;
}

/** Compact, dependency-free SVG energy diagram (catalysed vs uncatalysed). */
function EnergyDiagram() {
  // Geometry
  const W = 360;
  const H = 180;
  const padX = 30;
  const baselineY = 130; // reactants line
  const productY = 150; // products line (exothermic)
  const peakY = 40; // uncatalysed peak
  const catPeakY = 80; // catalysed peak
  const reactX = padX;
  const prodX = W - padX;
  const peakX = W / 2;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Energy profile diagram showing reactants, transition state, and products with and without a catalyst"
      className="w-full max-w-md"
    >
      {/* axes */}
      <line
        x1={padX - 10}
        y1={H - 15}
        x2={W - padX + 10}
        y2={H - 15}
        stroke="hsl(var(--border))"
      />
      <line
        x1={padX - 10}
        y1={20}
        x2={padX - 10}
        y2={H - 15}
        stroke="hsl(var(--border))"
      />
      <text
        x={padX - 14}
        y={18}
        fill="hsl(var(--muted-foreground))"
        fontSize="9"
        textAnchor="end"
      >
        Energy
      </text>
      <text
        x={W - padX}
        y={H - 3}
        fill="hsl(var(--muted-foreground))"
        fontSize="9"
        textAnchor="end"
      >
        Reaction progress →
      </text>

      {/* uncatalysed curve */}
      <path
        d={`M ${reactX} ${baselineY} Q ${peakX} ${peakY - 20} ${prodX} ${productY}`}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
      />
      {/* catalysed curve */}
      <path
        d={`M ${reactX} ${baselineY} Q ${peakX} ${catPeakY - 10} ${prodX} ${productY}`}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.4"
        strokeWidth="2"
        strokeDasharray="4 3"
      />

      {/* Ea arrow uncatalysed */}
      <line
        x1={peakX - 60}
        y1={baselineY}
        x2={peakX - 60}
        y2={peakY}
        stroke="hsl(var(--foreground))"
        strokeWidth="1"
        markerEnd="url(#arrow)"
        markerStart="url(#arrowStart)"
      />
      <text
        x={peakX - 56}
        y={(baselineY + peakY) / 2}
        fill="hsl(var(--foreground))"
        fontSize="10"
      >
        Ea
      </text>

      {/* Ea catalysed */}
      <line
        x1={peakX + 60}
        y1={baselineY}
        x2={peakX + 60}
        y2={catPeakY}
        stroke="hsl(var(--muted-foreground))"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <text
        x={peakX + 64}
        y={(baselineY + catPeakY) / 2}
        fill="hsl(var(--muted-foreground))"
        fontSize="10"
      >
        Ea (cat.)
      </text>

      {/* labels */}
      <text x={reactX} y={baselineY - 6} fill="hsl(var(--foreground))" fontSize="10">
        Reactants
      </text>
      <text
        x={prodX}
        y={productY + 14}
        fill="hsl(var(--foreground))"
        fontSize="10"
        textAnchor="end"
      >
        Products
      </text>

      {/* ΔH arrow */}
      <line
        x1={prodX - 15}
        y1={baselineY}
        x2={prodX - 15}
        y2={productY}
        stroke="hsl(var(--foreground))"
        strokeWidth="1"
        markerEnd="url(#arrow)"
      />
      <text
        x={prodX - 12}
        y={(baselineY + productY) / 2 + 4}
        fill="hsl(var(--foreground))"
        fontSize="10"
      >
        ΔH
      </text>

      <defs>
        <marker
          id="arrow"
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill="hsl(var(--foreground))" />
        </marker>
        <marker
          id="arrowStart"
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill="hsl(var(--foreground))" />
        </marker>
      </defs>
    </svg>
  );
}

export default function Kinetics() {
  const [enabledCategories, setEnabledCategories] = useState<Set<KinCategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [difficulty, setDifficulty] = useState<KinDifficulty | "all">("all");

  const pool = useMemo(
    () =>
      KIN_PROBLEMS.filter(
        (p) =>
          enabledCategories.has(p.category) &&
          (difficulty === "all" || p.difficulty === difficulty),
      ),
    [enabledCategories, difficulty],
  );

  const [current, setCurrent] = useState<KinProblem | null>(
    () => KIN_PROBLEMS[0] ?? null,
  );
  const [attempt, setAttempt] = useState<AttemptState>({
    problemId: KIN_PROBLEMS[0]?.id ?? "",
    selected: null,
    graded: false,
  });
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });

  const next = () => {
    if (pool.length === 0) return;
    let p = pickRandom(pool);
    if (pool.length > 1 && current && p.id === current.id) {
      p = pickRandom(pool.filter((q) => q.id !== current.id));
    }
    setCurrent(p);
    setAttempt({ problemId: p.id, selected: null, graded: false });
  };

  const submit = () => {
    if (!current || attempt.selected === null) return;
    const ok = attempt.selected === current.correct;
    setAttempt((a) => ({ ...a, graded: true }));
    setStats((s) => ({
      attempted: s.attempted + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
  };

  const toggleCategory = (c: KinCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else next.add(c);
      return next;
    });
  };

  const accuracy =
    stats.attempted === 0
      ? 0
      : Math.round((stats.correct / stats.attempted) * 100);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemical Kinetics — ElementPad" description="Reaction rates, rate laws, activation energy, and catalysis explained with interactive collision simulations." path="/learn/kinetics" />
      <AppHeader />
      <Main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
            <Gauge size={14} aria-hidden="true" />
            Conceptual unit
          </div>
          <h1 className="text-3xl font-bold text-foreground">Reaction Kinetics</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            How fast does a reaction go, and what makes it go faster? Collision
            theory, rate laws, activation energy, and catalysts.
          </p>
          <a
            href="/learn/kinetics/collisions"
            className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-primary hover:underline"
          >
            → Open the Collision Simulator
          </a>
        </header>

        {/* ── Lesson ──────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="lesson-heading"
          className="rounded-xl border border-border bg-tile-bg p-6 space-y-6"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-primary" aria-hidden="true" />
            <h2
              id="lesson-heading"
              className="text-lg font-semibold text-foreground"
            >
              Five factors that change reaction rate
            </h2>
          </div>

          <ol className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                t: "1. Concentration",
                d: "More particles per unit volume → more collisions per second → faster.",
              },
              {
                t: "2. Temperature",
                d: "Higher T = more KE, more particles exceed Ea, AND collisions are more frequent.",
              },
              {
                t: "3. Surface area",
                d: "For solids: powder reacts faster than a single lump because more atoms are exposed.",
              },
              {
                t: "4. Catalysts",
                d: "Provide an alternative pathway with lower Ea. Not consumed. Don't change ΔH or K.",
              },
              {
                t: "5. Nature of reactants",
                d: "Ionic reactions in solution are usually fast; reactions that break strong covalent bonds are slow.",
              },
            ].map((s) => (
              <li
                key={s.t}
                className="rounded-lg border border-border bg-background/40 p-3"
              >
                <p className="font-semibold text-foreground text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </li>
            ))}
          </ol>

          {/* Energy diagram + collision theory */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Collision theory in one breath
              </h3>
              <p className="text-xs text-muted-foreground">
                A reaction happens when particles collide with{" "}
                <strong>enough energy</strong> (≥ activation energy, Ea){" "}
                <strong>and</strong> the <strong>correct orientation</strong>.
                Most collisions don't satisfy both — that's why reactions take
                time.
              </p>
              <p className="font-mono text-xs text-foreground pt-1 border-t border-border">
                Arrhenius: k = A · exp(−Ea / RT)
              </p>
              <p className="text-[11px] text-muted-foreground">
                k = rate constant, A = pre-exponential, R = 8.314 J/mol·K, T in K.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-background/40 p-4 flex flex-col items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground self-start">
                Energy profile (with catalyst)
              </h3>
              <EnergyDiagram />
              <p className="text-[11px] text-muted-foreground text-center">
                Solid line = uncatalysed. Dashed line = catalysed (lower Ea).
                ΔH is unchanged.
              </p>
            </div>
          </div>

          <WorkedExample
            title="Determining a rate law from data"
            problem="For 2NO(g) + O₂(g) → 2NO₂(g): doubling [NO] (with [O₂] fixed) quadruples the rate. Doubling [O₂] (with [NO] fixed) doubles the rate. Find the rate law."
            steps={[
              {
                label: "Step 1: Find the order in NO",
                explain:
                  "Doubling [NO] → rate × 4. Solve 2^x = 4 → x = 2. So order in NO is 2.",
                formula: "2^x = 4  →  x = 2",
              },
              {
                label: "Step 2: Find the order in O₂",
                explain:
                  "Doubling [O₂] → rate × 2. Solve 2^y = 2 → y = 1. Order in O₂ is 1.",
                formula: "2^y = 2  →  y = 1",
              },
              {
                label: "Step 3: Combine into the rate law",
                explain:
                  "Rate = k × [each reactant raised to its order]. Coefficients in the balanced equation do NOT determine the rate law — only data does.",
                formula: "rate = k [NO]² [O₂]¹",
              },
              {
                label: "Step 4: State the overall order",
                explain: "Sum the exponents: 2 + 1 = 3. Reaction is third order overall.",
              },
            ]}
            answer="rate = k [NO]² [O₂],   overall third order"
          />
        </section>

        {/* ── Drill ───────────────────────────────────────────────────────── */}
        <section aria-labelledby="drill-heading" className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2
                id="drill-heading"
                className="text-lg font-semibold text-foreground"
              >
                Practice drill
              </h2>
              <p className="text-xs text-muted-foreground">
                Multiple choice, instant feedback with reasoning.
              </p>
            </div>
            <div
              className="text-xs font-mono text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground font-semibold">{stats.correct}</span>
              /{stats.attempted} correct
              {stats.attempted > 0 && (
                <span className="ml-2">({accuracy}% accuracy)</span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl border border-border bg-tile-bg p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Difficulty
              </p>
              <div className="flex flex-wrap gap-2">
                {(["all", ...ALL_DIFFICULTIES] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      difficulty === d
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = enabledCategories.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCategory(c)}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        on
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {KIN_CATEGORY_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Drill card */}
          {current ? (
            <article className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  {KIN_CATEGORY_LABELS[current.category]} · {current.difficulty}
                </span>
                <button
                  onClick={next}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Shuffle size={12} aria-hidden="true" />
                  New problem
                </button>
              </div>

              <p className="text-base text-foreground leading-relaxed">
                {current.prompt}
              </p>

              {current.formula && (
                <div className="rounded-lg border border-border bg-background/40 p-3 font-mono text-sm text-foreground">
                  {current.formula}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-2">
                {current.options.map((opt, i) => {
                  const isSelected = attempt.selected === i;
                  const isCorrect = i === current.correct;
                  let cls =
                    "border-border bg-background/40 hover:bg-muted/60 hover:border-muted-foreground/40";
                  if (attempt.graded) {
                    if (isCorrect)
                      cls =
                        "border-emerald-500/40 bg-emerald-500/10 text-emerald-400";
                    else if (isSelected)
                      cls =
                        "border-destructive/40 bg-destructive/10 text-destructive";
                    else cls = "border-border bg-background/40 opacity-60";
                  } else if (isSelected) {
                    cls = "border-primary bg-primary/10";
                  }
                  return (
                    <button
                      key={i}
                      disabled={attempt.graded}
                      onClick={() =>
                        setAttempt((a) => ({ ...a, selected: i }))
                      }
                      className={`text-left rounded-lg border p-3 text-sm transition-colors ${cls}`}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <span className="font-mono text-xs text-muted-foreground">
                          {String.fromCharCode(65 + i)}.
                        </span>
                        <span className="text-foreground flex-1">{opt}</span>
                        {attempt.graded && isCorrect && (
                          <Check
                            size={14}
                            className="text-emerald-400"
                            aria-hidden="true"
                          />
                        )}
                        {attempt.graded && isSelected && !isCorrect && (
                          <XIcon
                            size={14}
                            className="text-destructive"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                {!attempt.graded ? (
                  <button
                    onClick={submit}
                    disabled={attempt.selected === null}
                    className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Check answer
                  </button>
                ) : (
                  <button
                    onClick={next}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next problem
                    <ArrowRight size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {attempt.graded && (
                <div
                  role="status"
                  className={`rounded-lg border p-3 text-sm ${
                    attempt.selected === current.correct
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-destructive/30 bg-destructive/5"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      attempt.selected === current.correct
                        ? "text-emerald-400"
                        : "text-destructive"
                    }`}
                  >
                    {attempt.selected === current.correct
                      ? "Correct."
                      : `Correct answer: ${String.fromCharCode(65 + current.correct)}. ${current.options[current.correct]}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {current.insight}
                  </p>
                </div>
              )}
            </article>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No problems match your filters.
              <button
                onClick={() => {
                  setEnabledCategories(new Set(ALL_CATEGORIES));
                  setDifficulty("all");
                }}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <RefreshCw size={12} aria-hidden="true" />
                Reset filters
              </button>
            </div>
          )}
        </section>
      </Main>
    </div>
  );
}